import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  Search, 
  Download, 
  Eye,
  Trash2,
  Calendar,
  Users,
  BarChart3,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Briefcase,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Target,
  Grid,
  List,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

import { resultsAPI, offersAPI, handleApiError } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import SkillsRadarChart from '../../components/charts/SkillsRadarChart';

const ResultsPage = () => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const [results, setResults] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffer, setSelectedOffer] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [recommendationFilter, setRecommendationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'list'
  const [expandedOffers, setExpandedOffers] = useState(new Set());

  const recommendationLabelByKey = useMemo(
    () => ({
      recommended: t('results.filters.recommended'),
      consider: t('results.filters.consider'),
      not_recommended: t('results.filters.notRecommended')
    }),
    [t]
  );

  const normalizeRecommendationKey = useCallback((recommendation) => {
    const value = (recommendation || '').toString().trim().toLowerCase();
    if (['recommandé', 'recommended', 'recomendado'].includes(value)) return 'recommended';
    if (['à considérer', 'to consider', 'consider', 'a considerar'].includes(value)) return 'consider';
    if (['non recommandé', 'not recommended', 'no recomendado'].includes(value)) return 'not_recommended';
    return null;
  }, []);

  const formatResultDate = useCallback((dateValue) => {
    const locale =
      currentLanguage === 'fr'
        ? 'fr-FR'
        : currentLanguage === 'es'
          ? 'es-ES'
          : 'en-US';
    return new Date(dateValue).toLocaleDateString(locale);
  }, [currentLanguage]);

  // Initialize page with smart default selection
  const initializePage = useCallback(async () => {
    try {
      setLoading(true);
      
      // First fetch offers
      const response = await offersAPI.getAll();
      const offersList = response.data.offers || [];
      setOffers(offersList);
      
      if (offersList.length === 0) {
        setLoading(false);
        return;
      }
      
      // Try to find the best default offer to show
      let defaultOffer = 'all';
      
      // Check if any offers have results and select the most recent one with results
      try {
        for (const offer of offersList.slice(0, 5)) { // Check first 5 offers for performance
          const testResponse = await resultsAPI.getByOffer(offer.id, { limit: 1 });
          if (testResponse.data.results && testResponse.data.results.length > 0) {
            defaultOffer = offer.id.toString();
            break;
          }
        }
      } catch (error) {
        // Error checking for results, will show all results
        defaultOffer = 'all';
      }
      
      setSelectedOffer(defaultOffer);
      
    } catch (error) {
      // Error initializing page
      toast.error(t('results.errors.dataLoadFailed'));
      setLoading(false);
    }
  }, [t]);


  const fetchResults = useCallback(async () => {
    if (!selectedOffer) return;
    
    try {
      setLoading(true);
      const pageSize = 200;

      const buildParams = () => {
        const params = {
          sort: sortBy,
          order: sortOrder
        };

        if (scoreFilter !== 'all') {
          const [min, max] = scoreFilter.split('-').map(Number);
          params.min_score = min;
          params.max_score = max;
        }

        if (recommendationFilter !== 'all') {
          params.recommendation = recommendationFilter;
        }

        return params;
      };

      const params = buildParams();
      
      if (selectedOffer === 'all') {
        const fetchAllOfferResults = async (offer) => {
          let offset = 0;
          const collected = [];

          while (true) {
            const response = await resultsAPI.getByOffer(offer.id, {
              ...params,
              limit: pageSize,
              offset
            });
            const batch = response.data.results || [];

            collected.push(
              ...batch.map((result) => ({
                ...result,
                offer_title: offer.title,
                offer_id: offer.id,
                recommendation_key:
                  result.recommendation_key || normalizeRecommendationKey(result.recommendation)
              }))
            );

            if (!response.data.pagination?.hasMore || batch.length === 0) {
              break;
            }

            offset += pageSize;
          }

          return collected;
        };

        const fetched = await Promise.all(
          offers.map(async (offer) => {
            try {
              return await fetchAllOfferResults(offer);
            } catch (_) {
              return [];
            }
          })
        );

        const allResults = fetched.flat();
        
        allResults.sort((a, b) => {
          if (sortBy === 'score') {
            return sortOrder === 'desc' ? b.score - a.score : a.score - b.score;
          } else if (sortBy === 'created_at') {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
          }
          return 0;
        });
        
        setResults(allResults);
        
        const recommended = allResults.filter(r => r.recommendation_key === 'recommended').length;
        const consider = allResults.filter(r => r.recommendation_key === 'consider').length;
        const notRecommended = allResults.filter(r => r.recommendation_key === 'not_recommended').length;
        const avgScore = allResults.length > 0 
          ? allResults.reduce((sum, r) => sum + r.score, 0) / allResults.length 
          : 0;
        
        setStats({ total: allResults.length, recommended, consider, notRecommended, avgScore });
      } else {
        // For specific offer
        const response = await resultsAPI.getByOffer(selectedOffer, params);
        const resultsData = response.data.results || [];
        
        // Add offer info to results
        const selectedOfferData = offers.find(o => o.id.toString() === selectedOffer);
        const resultsWithOfferInfo = resultsData.map(result => ({
          ...result,
          offer_title: selectedOfferData?.title,
          offer_id: selectedOfferData?.id,
          recommendation_key: result.recommendation_key || normalizeRecommendationKey(result.recommendation)
        }));
        
        setResults(resultsWithOfferInfo);
        
        const statsPayload = response.data.statistics?.recommendations;
        const recommended = statsPayload?.recommended ?? resultsWithOfferInfo.filter(r => r.recommendation_key === 'recommended').length;
        const consider = statsPayload?.consider ?? resultsWithOfferInfo.filter(r => r.recommendation_key === 'consider').length;
        const notRecommended = statsPayload?.not_recommended ?? resultsWithOfferInfo.filter(r => r.recommendation_key === 'not_recommended').length;
        const total = Number(response.data.statistics?.total ?? resultsData.length);
        const averageFromStats = Number(response.data.statistics?.averageScore);
        const avgScore = Number.isFinite(averageFromStats)
          ? averageFromStats
          : resultsData.length > 0
            ? resultsData.reduce((sum, r) => sum + r.score, 0) / resultsData.length
            : 0;
        
        setStats({ total, recommended, consider, notRecommended, avgScore });
      }
    } catch (error) {
      // Error fetching results
      toast.error(handleApiError(error, t('results.errors.resultsLoadFailed')));
    } finally {
      setLoading(false);
    }
  }, [offers, recommendationFilter, scoreFilter, selectedOffer, sortBy, sortOrder, t, normalizeRecommendationKey]);

  useEffect(() => {
    initializePage();
  }, [initializePage]);

  useEffect(() => {
    if (selectedOffer && offers.length > 0) {
      fetchResults();
    }
  }, [fetchResults, offers.length, selectedOffer]);

  const filteredResults = useMemo(() => {
    if (!searchTerm) return results;
    const searchLower = searchTerm.toLowerCase();
    return results.filter((result) =>
      result.candidate_name?.toLowerCase().includes(searchLower) ||
      result.cv_filename?.toLowerCase().includes(searchLower) ||
      result.cv_name?.toLowerCase().includes(searchLower)
    );
  }, [results, searchTerm]);

  const groupedResults = useMemo(() => {
    if (viewMode !== 'grouped' || filteredResults.length === 0) return {};

    const grouped = filteredResults.reduce((acc, result) => {
      const offerId = result.offer_id;
      if (!acc[offerId]) {
        acc[offerId] = {
          offer: {
            id: offerId,
            title: result.offer_title,
            client: result.offer_client
          },
          results: [],
          stats: {
            total: 0,
            recommended: 0,
            consider: 0,
            notRecommended: 0,
            avgScore: 0,
            topScore: 0
          }
        };
      }
      acc[offerId].results.push(result);
      return acc;
    }, {});

    Object.keys(grouped).forEach((offerId) => {
      const group = grouped[offerId];
      const groupResults = [...group.results].sort((a, b) => b.score - a.score);
      group.results = groupResults;
      group.stats = {
        total: groupResults.length,
        recommended: groupResults.filter((r) => r.recommendation_key === 'recommended').length,
        consider: groupResults.filter((r) => r.recommendation_key === 'consider').length,
        notRecommended: groupResults.filter((r) => r.recommendation_key === 'not_recommended').length,
        avgScore:
          groupResults.length > 0
            ? Math.round(groupResults.reduce((sum, r) => sum + r.score, 0) / groupResults.length)
            : 0,
        topScore: groupResults.length > 0 ? Math.max(...groupResults.map((r) => r.score)) : 0
      };
    });

    return grouped;
  }, [filteredResults, viewMode]);

  useEffect(() => {
    const offerIds = Object.keys(groupedResults);
    if (offerIds.length === 1) {
      setExpandedOffers(new Set([offerIds[0]]));
    }
  }, [groupedResults]);

  // Toggle offer expansion
  const toggleOfferExpansion = (offerId) => {
    const newExpanded = new Set(expandedOffers);
    if (newExpanded.has(offerId)) {
      newExpanded.delete(offerId);
    } else {
      newExpanded.add(offerId);
    }
    setExpandedOffers(newExpanded);
  };

  // Get score color
  const getScoreTextClass = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'text-green-700 bg-green-50';
    if (score >= 60) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
  };

  // Get recommendation badge
  const getRecommendationBadge = (recommendationKey) => {
    const badges = {
      recommended: 'bg-green-100 text-green-800 border-green-200',
      consider: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      not_recommended: 'bg-red-100 text-red-800 border-red-200'
    };
    return badges[recommendationKey] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Render grouped results
  const renderGroupedResults = () => {
    const offerGroups = Object.values(groupedResults);
    
    if (offerGroups.length === 0) {
      return (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t('results.messages.noResults')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {offerGroups.map((group) => {
          const isExpanded = expandedOffers.has(group.offer.id.toString());
          
          return (
            <motion.div
              key={group.offer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border border-gray-200"
            >
              {/* Offer Header */}
              <div 
                className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleOfferExpansion(group.offer.id.toString())}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 p-3 bg-indigo-50 rounded-lg">
                    <Briefcase className="h-6 w-6 text-indigo-600" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {group.offer.title}
                    </h3>
                    {group.offer.client && (
                      <p className="text-sm text-gray-600">{group.offer.client}</p>
                    )}
                  </div>
                </div>
                
                {/* Stats Summary */}
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {group.stats.total}
                    </div>
                    <div className="text-xs text-gray-600">{t('results.labels.cvs')}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${getScoreTextClass(group.stats.topScore)}`}>
                      {group.stats.topScore}
                    </div>
                    <div className="text-xs text-gray-600">{t('results.labels.topScore')}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {group.stats.recommended}
                    </div>
                    <div className="text-xs text-gray-600">{t('results.labels.recommended')}</div>
                  </div>
                </div>
              </div>
              
              {/* Expanded Results */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-200"
                  >
                    <div className="p-6 space-y-4">
                      {group.results.map((result, index) => (
                        <motion.div
                          key={result.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="flex-shrink-0">
                              <FileText className="h-5 w-5 text-gray-400" />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-1">
                                <h4 className="font-medium text-gray-900">
                                  {result.candidate_name || t('results.labels.unknownCandidate')}
                                </h4>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRecommendationBadge(result.recommendation_key)}`}>
                                  {getRecommendationLabel(result.recommendation_key, result.recommendation)}
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span>{result.cv_name || result.cv_filename || t('results.labels.deletedCv')}</span>
                                <span>•</span>
                                <span>{t('results.labels.score')}: {result.score}/100</span>
                                <span>•</span>
                                <span>{formatResultDate(result.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <div className={`px-3 py-1 rounded-lg text-sm font-medium ${getScoreBadgeClass(result.score)}`}>
                              {result.score}
                            </div>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewResult(result);
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                              aria-label={t('results.actions.viewDetails')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(result.id);
                              }}
                              className="p-2 text-red-400 hover:text-red-600 transition-colors"
                              aria-label={t('results.actions.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    );
  };

  // Handle result deletion
  const handleDelete = async (resultId) => {
    const result = await confirm({
      title: t('results.deleteModal.title'),
      message: t('results.deleteModal.message'),
      type: 'danger',
      confirmText: t('results.deleteModal.confirm'),
      cancelText: t('results.deleteModal.cancel')
    });

    if (!result) return;

    try {
      await resultsAPI.delete(resultId);
      toast.success(t('results.messages.deleteSuccess'));
      fetchResults();
    } catch (error) {
      const errorMessage = handleApiError(error, t('results.errors.deleteFailed'));
      toast.error(errorMessage);
    }
  };

  // View detailed result
  const handleViewResult = async (result) => {
    setSelectedResult(result);
    setShowDetailModal(true);
    setDetailLoading(true);

    try {
      const response = await resultsAPI.getDetail(result.id);
      const detail = response.data || {};

      setSelectedResult({
        ...result,
        offer_title: detail.offer?.title ?? result.offer_title,
        offer_client: detail.offer?.client ?? result.offer_client,
        cv_name: detail.cv?.name ?? result.cv_name,
        cv_filename: detail.cv?.filename ?? result.cv_filename,
        score: detail.analysis?.score ?? result.score,
        analysis: detail.analysis?.details ?? result.analysis,
        strengths: detail.analysis?.strengths ?? result.strengths,
        weaknesses: detail.analysis?.weaknesses ?? result.weaknesses,
        recommendation: detail.analysis?.recommendation ?? result.recommendation,
        recommendation_key:
          detail.analysis?.recommendation_key ||
          normalizeRecommendationKey(detail.analysis?.recommendation ?? result.recommendation),
        skills_radar: detail.analysis?.skills_radar ?? result.skills_radar,
        created_at: detail.created_at ?? result.created_at
      });
    } catch (error) {
      toast.error(handleApiError(error, t('results.errors.resultsLoadFailed')));
    } finally {
      setDetailLoading(false);
    }
  };

  // Export results
  const handleExportResults = async () => {
    if (!selectedOffer) return;
    
    if (selectedOffer === 'all') {
      // For all offers, we can't use the single offer export endpoint
      // We would need to implement a different export strategy
      toast.info(t('results.messages.exportAllNotAvailable'));
      return;
    }
    
    try {
      const response = await resultsAPI.exportCsv(selectedOffer);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const offer = offers.find(o => o.id.toString() === selectedOffer);
      link.download = `resultats-${offer?.title.replace(/[^a-zA-Z0-9]/g, '-') || 'offre'}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('results.messages.exportSuccess'));
    } catch (error) {
      toast.error(t('results.errors.exportFailed'));
    }
  };

  // Get recommendation color
  const getRecommendationColor = (recommendationKey) => {
    switch (recommendationKey) {
      case 'recommended': return 'text-green-600 bg-green-100';
      case 'consider': return 'text-yellow-600 bg-yellow-100';
      case 'not_recommended': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRecommendationLabel = useCallback(
    (recommendationKey, fallbackLabel) => {
      return recommendationLabelByKey[recommendationKey] || fallbackLabel || t('results.filters.allRecommendations');
    },
    [recommendationLabelByKey, t]
  );


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
                {t('results.title')}
              </h1>
              {user?.role === 'rh' && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                  <Users className="w-3 h-3 mr-1" />
                  {t('results.labels.team')}
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">
              {user?.role === 'admin' 
                ? offers.length > 0 
                  ? t('results.subtitle.adminWithResults')
                      .replace('{{count}}', results.length)
                      .replace('{{offerCount}}', offers.length)
                  : t('results.subtitle.adminNoOffers')
                : results.length > 0
                  ? t('results.subtitle.hrWithResults')
                      .replace('{{count}}', results.length)
                  : t('results.subtitle.hrNoResults')
              }
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={initializePage}
              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('results.actions.refresh')}
            </button>
            {results.length > 0 && (
              <button
                onClick={handleExportResults}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                disabled={selectedOffer === 'all'}
                title={selectedOffer === 'all' ? t('results.actions.exportNotAvailable') : t('results.actions.exportTitle')}
              >
                <Download className="w-4 h-4 mr-2" />
                {t('results.actions.export')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats.total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('results.stats.total')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Users className="h-7 w-7 text-indigo-600" />
              </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('results.stats.recommended')}</p>
                  <p className="text-2xl font-bold text-green-600">{stats.recommended}</p>
                </div>
                <ThumbsUp className="h-8 w-8 text-green-600" />
              </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('results.stats.consider')}</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.consider}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('results.stats.avgScore')}</p>
                  <p className="text-2xl font-bold text-indigo-600">{stats.avgScore.toFixed(1)}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-indigo-600" />
              </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Offer Selection */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('results.filters.offer')}
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  value={selectedOffer}
                  onChange={(e) => setSelectedOffer(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-10 py-2 text-sm"
                >
                  <option value="all">{t('results.filters.allOffers')}</option>
                  {offers.map((offer) => (
                    <option key={offer.id} value={offer.id.toString()}>
                      {offer.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('results.filters.search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('results.filters.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-10 py-2 text-sm"
                />
              </div>
            </div>

            {/* Score Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('results.filters.score')}
              </label>
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">{t('results.filters.allScores')}</option>
                <option value="80-100">{t('results.filters.excellent')}</option>
                <option value="60-79">{t('results.filters.good')}</option>
                <option value="40-59">{t('results.filters.average')}</option>
                <option value="0-39">{t('results.filters.weak')}</option>
              </select>
            </div>

            {/* Recommendation Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('results.filters.recommendation')}
              </label>
              <select
                value={recommendationFilter}
                onChange={(e) => setRecommendationFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">{t('results.filters.allRecommendations')}</option>
                <option value="recommended">{t('results.filters.recommended')}</option>
                <option value="consider">{t('results.filters.consider')}</option>
                <option value="not_recommended">{t('results.filters.notRecommended')}</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('results.filters.sortBy')}
              </label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="score-desc">{t('results.filters.scoreDesc')}</option>
                <option value="score-asc">{t('results.filters.scoreAsc')}</option>
                <option value="created_at-desc">{t('results.filters.newest')}</option>
                <option value="created_at-asc">{t('results.filters.oldest')}</option>
              </select>
            </div>
          </div>
      </div>

      {/* View Mode Toggle */}
      {filteredResults.length > 0 && (
        <div className="flex justify-end mb-6">
          <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grouped')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grouped'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid className="w-4 h-4 mr-2 inline" />
{t('results.viewModes.byOffer')}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4 mr-2 inline" />
{t('results.viewModes.list')}
            </button>
          </div>
        </div>
      )}

      {/* Results Display */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">
            {offers.length === 0 ? t('results.loading.offers') : t('results.loading.results')}
          </p>
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-12">
          <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('results.emptyStates.noOffers.title')}</h3>
          <p className="text-gray-600 mb-6">
            {t('results.emptyStates.noOffers.description')}
          </p>
          <button
            onClick={() => window.location.href = '/dashboard/offers'}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Briefcase className="w-4 h-4 mr-2" />
{t('results.actions.createOffer')}
          </button>
        </div>
      ) : filteredResults.length === 0 ? (
        <div className="text-center py-12">
          <TrendingUp className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || scoreFilter !== 'all' || recommendationFilter !== 'all'
              ? t('results.emptyStates.noFilterResults.title')
              : t('results.emptyStates.noAnalysis.title')
            }
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || scoreFilter !== 'all' || recommendationFilter !== 'all'
              ? t('results.emptyStates.noFilterResults.description')
              : selectedOffer === 'all' 
                ? t('results.emptyStates.noAnalysis.descriptionAll')
                : t('results.emptyStates.noAnalysis.descriptionSpecific')
            }
          </p>
          {!(searchTerm || scoreFilter !== 'all' || recommendationFilter !== 'all') && (
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => window.location.href = '/dashboard/analyzer'}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
{t('results.actions.analyzeCvs')}
              </button>
              {selectedOffer !== 'all' && (
                <button
                  onClick={() => setSelectedOffer('all')}
                  className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-200 hover:bg-indigo-50"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
{t('results.actions.viewAllAnalyses')}
                </button>
              )}
            </div>
          )}
        </div>
      ) : viewMode === 'grouped' ? (
        renderGroupedResults()
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="space-y-4">
              {filteredResults.map((result, index) => (
                <motion.div
                  key={result.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 text-indigo-700 rounded-full font-bold">
                      {index + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {result.candidate_name || t('results.labels.unknownCandidate')}
                      </h3>
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {result.cv_name || result.cv_filename || t('results.labels.deletedCv')}
                      </p>
                      {selectedOffer === 'all' && result.offer_title && (
                        <p className="text-sm text-gray-500 truncate mt-1">
                          {t('results.labels.offer')}: {result.offer_title}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 mt-1">
                        <span className={`text-xl font-bold ${getScoreTextClass(result.score)}`}>
                          {result.score}/100
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRecommendationColor(result.recommendation_key)}`}>
                          {getRecommendationLabel(result.recommendation_key, result.recommendation)}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatResultDate(result.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewResult(result)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50"
                      title={t('results.actions.viewDetails')}
                      aria-label={t('results.actions.viewDetails')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(result.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50"
                      title={t('results.actions.delete')}
                      aria-label={t('results.actions.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedResult && (
        <div className="fixed inset-0 bg-black/40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border border-gray-200 w-full max-w-5xl shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('results.detailModal.title').replace('{{filename}}', selectedResult.candidate_name || t('results.labels.unknownCandidate'))}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedResult.cv_name || selectedResult.cv_filename || t('results.labels.deletedCv')}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className={`text-4xl font-bold ${getScoreTextClass(selectedResult.score)}`}>
                  {selectedResult.score}/100
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.globalScore')}</p>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRecommendationColor(selectedResult.recommendation_key)}`}>
                  {getRecommendationLabel(selectedResult.recommendation_key, selectedResult.recommendation)}
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.recommendation')}</p>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">
                  {t('results.detailModal.analyzedOn').replace('{{date}}', formatResultDate(selectedResult.created_at))}
                </div>
                <p className="text-gray-600 mt-1">{t('results.detailModal.analysisDate')}</p>
              </div>
            </div>

            <div className="space-y-6">
              {detailLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('common.loading') || 'Loading...'}
                </div>
              )}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t('results.detailModal.detailedAnalysis')}</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.analysis}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                    {t('results.detailModal.strengths')}
                  </h4>
                  <div className="bg-green-50 p-4 rounded-md">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.strengths}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <ThumbsDown className="w-4 h-4 mr-2 text-red-600" />
                    {t('results.detailModal.weaknesses')}
                  </h4>
                  <div className="bg-red-50 p-4 rounded-md">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedResult.weaknesses}</p>
                  </div>
                </div>
              </div>

              {/* Radar Chart */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Target className="w-4 h-4 mr-2 text-blue-600" />
                  {t('results.detailModal.skillsEvaluation')}
                </h4>
                <SkillsRadarChart 
                  data={selectedResult.skills_radar || []} 
                  title={t('results.detailModal.skillsRadarTitle')}
                />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
{t('results.detailModal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default ResultsPage;
